import React from 'react'
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

/**
 * Catches render errors in the component tree below it so a single
 * throwing component shows a recoverable fallback instead of blanking
 * the entire app (which looks like a crash to users and App reviewers).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info)
  }

  // The boundary sits outside the route switch, so it stays mounted across
  // navigation and would keep showing the fallback on pages that are fine.
  // Clearing on a path change lets the next route render. No-op unless an
  // error is actually showing, so healthy pages are never remounted.
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.pathname !== this.props.pathname) {
      this.setState({ error: null })
    }
  }

  handleReset = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      const isDev = import.meta.env.VITE_ENVIRONMENT === 'dev'
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Something went wrong</h1>
          <p className="text-slate-400 text-sm mb-6 max-w-md">
            This page ran into an error. Try again, or head back to the dashboard.
          </p>
          {isDev && (
            <pre className="text-left text-xs text-red-400 bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6 max-w-full overflow-auto whitespace-pre-wrap">
              {this.state.error?.message || String(this.state.error)}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
          )}
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Try Again
            </button>
            <a
              href="/"
              className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg"
            >
              Dashboard
            </a>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
